import type { TranslationString } from "../translationStore";
import type { TranslationBackend } from "./index";

// localStorage keys for the NR session token (written by plugins/nr-token.client.ts)
export const TOKEN_KEY = "nr-translation-token";
export const LOGIN_KEY = "nr-translation-login";

// HTTP/REST backend implementation
export class HttpBackend implements TranslationBackend {
  // Server time of the last fetch per system+lang, held until the store
  // confirms the data was persisted (commitSyncCursor) — writing the cursor
  // before the save lands means a failed save silently loses those strings
  // from every later delta fetch
  private pendingSince = new Map<string, number>();

  // Metadata of the last fetch per system+lang (see lastFetchInfo)
  private lastFetch = new Map<string, { total: number | null; full: boolean }>();

  constructor(private baseUrl: string) {}

  private getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    const token = typeof localStorage !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    return headers;
  }

  // Sliding renewal: the server hands back a fresh session token when ours is old
  private captureRenewal(response: Response) {
    const renewed = response.headers.get("x-renewed-token");
    if (renewed && typeof localStorage !== "undefined") {
      localStorage.setItem(TOKEN_KEY, renewed);
    }
  }

  // Surface the server's human-readable message — the UI shows it verbatim,
  // so server-side policy changes reach users without a client update
  private async apiError(response: Response, action: string): Promise<Error> {
    let message = "";
    try {
      const body = await response.json();
      message = body?.statusMessage || body?.message || "";
    } catch {
      try {
        message = await response.text();
      } catch {}
    }
    return new Error(message || `${action} failed (${response.status})`);
  }

  // Delta bookkeeping: last successful sync's SERVER timestamp per system+lang
  private sinceKey(systemId: string, languageCode: string): string {
    return `nr-last-sync-${systemId}-${languageCode}`;
  }

  // Re-fetch a 60s overlap (writes racing the previous read are cheap to
  // re-apply, missing them is not). Deletions don't need a cursor expiry:
  // the store compares the server's string count against local state and
  // forces a full resync on divergence (see syncFromBackend).
  private getSince(systemId: string, languageCode: string): number | undefined {
    if (typeof localStorage === "undefined") return undefined;
    const stored = Number(localStorage.getItem(this.sinceKey(systemId, languageCode)));
    if (!stored) return undefined;
    return stored - 60_000;
  }

  async fetchTranslations(
    systemId: string,
    languageCode: string,
    opts?: { delta?: boolean; totalStrings?: number }
  ): Promise<TranslationString[]> {
    const url = `${this.baseUrl}/translations`;
    const headers = this.getAuthHeaders();

    const since = opts?.delta ? this.getSince(systemId, languageCode) : undefined;
    const requestBody = {
      systemId,
      languageCode,
      ...(since ? { since } : {}),
      ...(opts?.totalStrings ? { totalStrings: opts.totalStrings } : {}),
    };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw await this.apiError(response, "Fetch");
      }

      const data = await response.json();
      // Any successful response (delta from our cursor, or full) covers
      // everything up to the server's `now` — but the cursor only advances
      // once the caller commits (after persisting the data locally)
      if (data.now) {
        this.pendingSince.set(this.sinceKey(systemId, languageCode), Number(data.now));
      }
      this.lastFetch.set(this.sinceKey(systemId, languageCode), {
        total: typeof data.total === "number" ? data.total : null,
        full: !since,
      });
      return data.translations || [];
    } catch (error) {
      console.error("Fetch error:", error);
      throw error;
    }
  }

  lastFetchInfo(systemId: string, languageCode: string): { total: number | null; full: boolean } | undefined {
    return this.lastFetch.get(this.sinceKey(systemId, languageCode));
  }

  commitSyncCursor(systemId: string, languageCode: string): void {
    const key = this.sinceKey(systemId, languageCode);
    const now = this.pendingSince.get(key);
    if (now && typeof localStorage !== "undefined") {
      localStorage.setItem(key, String(now));
      this.pendingSince.delete(key);
    }
  }

  async reportManifest(systemId: string, keys: string[], sha?: string): Promise<{ orphaned: number; restored: number }> {
    const response = await fetch(`${this.baseUrl}/translations`, {
      method: "POST",
      headers: this.getAuthHeaders(),
      // languageCode is required by the endpoint but the manifest is
      // system-wide — any value works
      body: JSON.stringify({ systemId, languageCode: "en", manifestKeys: keys, ...(sha ? { manifestSha: sha } : {}) }),
    });
    if (!response.ok) throw await this.apiError(response, "Manifest report");
    const data = await response.json().catch(() => null);
    return { orphaned: data?.orphaned || 0, restored: data?.restored || 0 };
  }

  // Chunked: one giant POST freezes the server (dev D1 proxy chokes, and a
  // 10k-string request gives no feedback and no retry granularity). Chunks
  // are sequential; a mid-way failure leaves earlier chunks submitted, which
  // is safe — resubmitting identical strings is a server-side no-op.
  static readonly UPLOAD_CHUNK = 500;

  async uploadTranslations(
    systemId: string,
    languageCode: string,
    translations: TranslationString[],
    onProgress?: (done: number, total: number) => void,
    totalStrings?: number
  ): Promise<{ count: number; suggestions: number }> {
    const url = `${this.baseUrl}/translations`;
    const headers = this.getAuthHeaders();
    const total = translations.length;
    const result = { count: 0, suggestions: 0 };

    try {
      for (let i = 0; i < total; i += HttpBackend.UPLOAD_CHUNK) {
        const chunk = translations.slice(i, i + HttpBackend.UPLOAD_CHUNK);
        const response = await fetch(url, {
          method: "POST",
          headers,
          // totalStrings rides the first chunk only — once per submit is enough
          body: JSON.stringify({ systemId, languageCode, translations: chunk, ...(i === 0 && totalStrings ? { totalStrings } : {}) }),
        });

        if (!response.ok) {
          throw await this.apiError(response, "Submit");
        }
        this.captureRenewal(response);

        const data = await response.json().catch(() => null);
        result.count += data?.count ?? chunk.length;
        result.suggestions += data?.suggestions ?? 0;
        onProgress?.(Math.min(i + chunk.length, total), total);
      }
      return result;
    } catch (error) {
      console.error("Upload error:", error);
      throw error;
    }
  }

  isAvailable(): boolean {
    return !!this.baseUrl;
  }

  async getStats(systemId: string): Promise<any> {
    try {
      // GET so browser and edge caches apply (stats need no auth)
      const url = `${this.baseUrl}/stats?systemId=${encodeURIComponent(systemId)}`;
      const response = await fetch(url);

      if (!response.ok) {
        return null;
      }

      const data = await response.json();

      // Server returns languages as an array of {languageCode, translatedStrings, ...};
      // statsStore expects a map keyed by language code with {translated, reviewed}
      const languages: Record<string, { translated: number; reviewed: number }> = {};
      for (const lang of data.languages || []) {
        languages[lang.languageCode] = {
          translated: lang.translatedStrings || 0,
          reviewed: 0,
        };
      }

      return { totalStrings: data.totalStrings, languages };
    } catch (error) {
      console.warn("Failed to fetch stats from HTTP backend:", error);
      return null;
    }
  }
}
